// src/components/Footer.tsx
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowUp, ExternalLink, Heart } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Define navigation links to match the same structure as in Navbar
  const navLinks = [
    { name: 'Domov', path: '/' },
    { name: 'O nas', path: '/o-nas' },
    { name: 'Avtoservis', path: '/avtoservis' },
    { name: 'Obnova turbo polnilnikov', path: '/obnova-turbo' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <footer className="bg-gray-900 text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Avtoservis Vodnik */}
          <Card className="bg-gray-800 border-gray-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue card-glow">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-autoBlue">Avtoservis Vodnik</h3>
              <p className="mb-4 text-gray-300">Vaš zanesljiv partner za servis in popravilo vseh znamk vozil. Smo strokovnjaki z več kot 25 let izkušenj.</p>
            </CardContent>
          </Card>

          {/* Kontakt */}
          <Card className="bg-gray-800 border-gray-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue card-glow">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-autoBlue">Kontakt</h3>
              <ul className="space-y-3">
                <li className="flex items-start group">
                  <MapPin size={20} className="mr-2 mt-1 flex-shrink-0 text-autoBlue group-hover:text-autoTeal transition-colors duration-300" />
                  <span className="text-gray-300 group-hover:text-white transition-colors duration-300">Bukovec 10A, 2314 Zgornja Polskava</span>
                </li>
                <li className="flex items-center group">
                  <Phone size={20} className="mr-2 flex-shrink-0 text-autoBlue group-hover:text-autoTeal transition-colors duration-300" />
                  <a href="tel:041273738" className="text-gray-300 hover:text-autoTeal transition-colors duration-300">041 273 738</a>
                </li>
                <li className="flex items-center group">
                  <Mail size={20} className="mr-2 flex-shrink-0 text-autoBlue group-hover:text-autoTeal transition-colors duration-300" />
                  <a href="mailto:milan_acko@t-2.net" className="text-gray-300 hover:text-autoTeal transition-colors duration-300">milan_acko@t-2.net</a>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Delovni čas */}
          <Card className="bg-gray-800 border-gray-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue card-glow">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-autoBlue">Delovni čas</h3>
              <ul className="space-y-3">
                <li className="flex items-center group">
                  <Clock size={20} className="mr-2 flex-shrink-0 text-autoBlue group-hover:text-autoTeal transition-colors duration-300" />
                  <div className="text-gray-300 group-hover:text-white transition-colors duration-300">
                    <p>Pon - Pet: 8:00 - 17:00</p>
                    <p>Sob: 8:00 - 12:00</p>
                    <p>Ned: Zaprto</p>
                  </div>
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-700">
                <h4 className="text-sm font-semibold mb-2 text-autoBlue">Pravne informacije</h4>
                <ul className="space-y-1">
                  <li className="transform hover:translate-x-2 transition-transform duration-300">
                    <Link 
                      to="/gdpr" 
                      onClick={scrollToTop}
                      className="text-gray-300 hover:text-autoTeal text-sm flex items-center transition-colors duration-300"
                    >
                      <ExternalLink size={12} className="mr-1" />
                      GDPR Politika
                    </Link>
                  </li>
                  <li className="transform hover:translate-x-2 transition-transform duration-300">
                    <Link 
                      to="/cookies" 
                      onClick={scrollToTop}
                      className="text-gray-300 hover:text-autoTeal text-sm flex items-center transition-colors duration-300"
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Piškotki
                    </Link>
                  </li>
                  <li className="transform hover:translate-x-2 transition-transform duration-300">
                    <Link 
                      to="/terms" 
                      onClick={scrollToTop}
                      className="text-gray-300 hover:text-autoTeal text-sm flex items-center transition-colors duration-300"
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Pogoji uporabe
                    </Link>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Navigacija */}
          <Card className="bg-gray-800 border-gray-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue card-glow">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-autoBlue">Navigacija</h3>
              <ul className="space-y-2">
                {navLinks.map((link, index) => (
                  <li key={index} className="transform hover:translate-x-2 transition-transform duration-300">
                    <Link 
                      to={link.path}
                      onClick={scrollToTop}
                      className="text-gray-300 hover:text-autoTeal flex items-center transition-colors duration-300"
                    >
                      <ExternalLink size={14} className="mr-2" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-6 relative">
          <div className="flex flex-col items-center md:flex-row md:justify-between">
            <p className="text-gray-400 order-2 md:order-1">
              © {new Date().getFullYear()} Avtoservis Vodnik. Vse pravice pridržane.
            </p>
            <p className="mt-4 md:mt-0 flex items-center gap-1 text-gray-400 order-1 md:order-2 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
              Made with <Heart size={16} className="text-autoRed animate-pulse" /> by <a href="#" className="text-autoBlue hover:text-autoTeal transition-colors">DigitalPro</a>
            </p>
          </div>
          <button 
            onClick={scrollToTop}
            className="scroll-to-top mt-4 md:mt-0 fixed bottom-8 right-8 bg-autoBlue text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-700 z-50"
            aria-label="Scroll to top"
          >
            <ArrowUp size={20} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;